// Servicio para subir y obtener imágenes de S3
import { S3_CONFIG } from '../config/api';
import { saveCachedImages } from '../utils/cmsCache';
import { getCmsImagesMetadata } from './cmsService';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// Extensiones soportadas en orden de prioridad
const SUPPORTED_EXTENSIONS = ['jpg', 'png', 'webp', 'gif'];

// Mapeo de secciones a nombres de archivo
const SECTION_FILE_NAMES = {
  hero_bg: 'hero_bg',
  banner_bg: 'banner_bg',
  carousel_1: 'carousel_1',
  carousel_2: 'carousel_2',
  carousel_3: 'carousel_3',
  carousel_4: 'carousel_4',
  carousel_5: 'carousel_5',
  event_1: 'event_1',
  event_2: 'event_2',
  event_3: 'event_3',
  event_4: 'event_4',
  page_hero_rules: 'page_hero_rules',
  page_hero_events: 'page_hero_events',
  page_hero_members: 'page_hero_members',
  page_hero_shop: 'page_hero_shop',
  page_hero_associates: 'page_hero_associates',
  page_hero_records: 'page_hero_records',
  page_hero_rankings: 'page_hero_rankings',
  page_hero_clasificacion: 'page_hero_clasificacion',
  page_hero_login: 'page_hero_login',
  page_hero_profile: 'page_hero_profile'
};

// Validar archivo antes de subir
export const validateFile = (file) => {
  if (!file) {
    return { valid: false, error: 'No se seleccionó ningún archivo' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Tipo de archivo no permitido. Use: JPG, PNG, GIF o WebP`
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `El archivo es demasiado grande (${sizeMB}MB). Máximo: 10MB`
    };
  }

  return { valid: true };
};

// Obtener URL pública de una imagen
export const getImageUrl = (section) => {
  const baseFileName = SECTION_FILE_NAMES[section];
  if (!baseFileName) return null;

  // Intentamos con jpg por defecto, pero la imagen real podría tener otra extensión
  return `${S3_CONFIG.BASE_URL}${baseFileName}`;
};

// Obtener URL de imagen del CMS. Se pide al backend, que entrega la URL con ?v=<fecha de
// subida> (si la imagen se reemplaza, la URL cambia y el navegador no usa una copia antigua).
// Si el backend no responde, se busca directo en S3 como antes.
export const getS3ImageUrl = async (section) => {
  const baseFileName = SECTION_FILE_NAMES[section];
  if (!baseFileName) return null;

  try {
    const meta = await getCmsImagesMetadata();
    if (Object.prototype.hasOwnProperty.call(meta, section)) return meta[section]?.url || null;
  } catch (e) {
    /* respaldo: búsqueda directa en S3 */
  }

  // Intentar cada extensión hasta encontrar una que exista
  for (const ext of SUPPORTED_EXTENSIONS) {
    const url = `${S3_CONFIG.BASE_URL}home/${baseFileName}.${ext}`;
    try {
      const response = await fetch(url, { method: 'HEAD' });
      if (response.ok) {
        saveCachedImages({ [section]: url });
        return url;
      }
    } catch (e) {
      continue;
    }
  }
  saveCachedImages({ [section]: null });
  return null; // No existe imagen para esta sección
};

// Obtener todas las imágenes del CMS desde S3
export const getAllCMSImages = async () => {
  const sections = Object.keys(SECTION_FILE_NAMES);
  const images = {
    hero_bg: null,
    banner_bg: null,
    carousel: [],
    events: [],
    page_hero_rules: null,
    page_hero_events: null,
    page_hero_members: null,
    page_hero_shop: null,
    page_hero_associates: null,
    page_hero_records: null,
    page_hero_rankings: null,
    page_hero_clasificacion: null,
    page_hero_login: null,
    page_hero_profile: null
  };

  // Obtener todas las imágenes en paralelo
  const results = await Promise.all(
    sections.map(async (section) => ({
      section,
      url: await getS3ImageUrl(section)
    }))
  );

  // Organizar resultados
  results.forEach(({ section, url }) => {
    if (section === 'hero_bg') {
      images.hero_bg = url;
    } else if (section === 'banner_bg') {
      images.banner_bg = url;
    } else if (section.startsWith('carousel_')) {
      const index = parseInt(section.split('_')[1]) - 1;
      images.carousel[index] = { id: index + 1, image: url };
    } else if (section.startsWith('event_')) {
      const index = parseInt(section.split('_')[1]) - 1;
      images.events[index] = { id: index + 1, image: url };
    } else if (section.startsWith('page_hero_') && images.hasOwnProperty(section)) {
      images[section] = url;
    }
  });

  return images;
};

const s3Service = {
  validateFile,
  getImageUrl,
  getS3ImageUrl,
  getAllCMSImages
};

export default s3Service;
